/**
 * useLocale Composable — Donor Portal
 *
 * Convenience wrapper that combines vue-i18n's `useI18n()` with
 * the locale Pinia store.  Consuming components get a stable `t` function,
 * a reactive `locale` ref, and store-driven helpers — all without importing
 * from two separate places.
 */
import { computed } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ComposerTranslation } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useLocaleStore } from '@/stores/locale.store';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/core/plugins/i18n';

type LanguageOption = { code: string; name: string; nativeName: string; direction: string };

type UseLocaleReturn = {
  t: ComposerTranslation;
  locale: ComputedRef<string>;
  currentLanguageName: Ref<string>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  isRtl: Ref<boolean>;
  availableLanguages: Ref<LanguageOption[]>;
  setLanguage: (lang: string) => Promise<void>;
  reloadBundle: () => Promise<void>;
};

export function useLocale(): UseLocaleReturn {
  const { t } = useI18n();
  const localeStore = useLocaleStore();
  const { currentLanguage, isLoading, error, isRtl, availableLanguages, currentLanguageName } =
    storeToRefs(localeStore);

  // Keep a computed ref so consumers always read the Pinia-managed value.
  const locale = computed(() => currentLanguage.value);

  async function setLanguage(lang: string): Promise<void> {
    if (!SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      console.warn(`[useLocale] Unsupported language: "${lang}". Supported: ${SUPPORTED_LANGUAGES.join(', ')}`);
      return;
    }
    // changeLanguage() (called inside the store) updates i18n.global.locale directly,
    // so i18nLocale stays in sync automatically. Only assign here on success would
    // risk desyncing on failure — let the store own the locale state entirely.
    await localeStore.setLanguage(lang as SupportedLanguage);
  }

  async function reloadBundle(): Promise<void> {
    await localeStore.reloadAfterOverrideChange();
  }

  return {
    t,
    locale,
    currentLanguageName,
    isLoading,
    error,
    isRtl,
    availableLanguages,
    setLanguage,
    reloadBundle,
  };
}
